import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';

const couponDefinitions = {
  SPECIAL100: { type: 'flat', amount: 100, description: 'Flat ₹100 off' },
  SAVE10: { type: 'percent', amount: 10, description: '10% off' },
  FLAT500: { type: 'flat', amount: 500, description: 'Flat ₹500 off' },
};

const parseCustomCoupon = (code) => {
  const m = code.match(/^special(\d+)$/i);
  if (m) {
    return { type: 'flat', amount: Number(m[1]), description: `Flat ₹${m[1]} off` };
  }
  return null;
};

const SPECIALIST_MULTIPLIERS = {
  'Top Scholar': 1.2,
  'Proficient Researcher': 1.1,
  'Skilled Academic Writer': 1,
};

const Specialist = () => {
  const { state } = useLocation();
  const navigate = useNavigate();

  const [name, setName] = useState('');
  const [mobile, setMobile] = useState('');
  const [selectedLevel, setSelectedLevel] = useState('Top Scholar');
  const [couponCode, setCouponCode] = useState('');
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [couponError, setCouponError] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [formspreeStatus, setFormspreeStatus] = useState(null);
  const [isSubmittingPayLater, setIsSubmittingPayLater] = useState(false);
  const suggestionsRef = useRef(null);

  useEffect(() => {
    const onClick = (e) => {
      if (suggestionsRef.current && !suggestionsRef.current.contains(e.target)) {
        setShowSuggestions(false);
      }
    };
    window.addEventListener('click', onClick);
    return () => window.removeEventListener('click', onClick);
  }, []);

  if (!state) {
    return (
      <div className="min-h-screen bg-[#02042b] flex items-center justify-center">
        <p className="p-8 text-center text-white">
          No previous data found.{' '}
          <Link to="/checkout/order-details" className="text-yellow-400 underline">
            Start again
          </Link>
        </p>
      </div>
    );
  }

  const { totalPrice: taskTotalPrice = 0 } = state;
  const levelMultiplier = SPECIALIST_MULTIPLIERS[selectedLevel] || 1;
  const baseSubtotal = Math.round(taskTotalPrice * levelMultiplier);

  let discountAmount = 0;
  if (appliedCoupon) {
    if (appliedCoupon.type === 'flat') {
      discountAmount = Math.min(appliedCoupon.amount, baseSubtotal);
    } else if (appliedCoupon.type === 'percent') {
      discountAmount = Math.floor((baseSubtotal * appliedCoupon.amount) / 100);
    }
  }

  const discountedSubtotal = Math.max(0, baseSubtotal - discountAmount);
  const processingFee = +(0.18 * discountedSubtotal).toFixed(2);
  const total = +(discountedSubtotal + processingFee).toFixed(2);

  const input = couponCode.trim().toUpperCase();
  let filteredSuggestions = Object.entries(couponDefinitions);
  if (input) {
    filteredSuggestions = Object.entries(couponDefinitions).filter(([code]) =>
      code.startsWith(input)
    );
    if (/^SPECIAL\d*$/i.test(input)) {
      const custom = parseCustomCoupon(input);
      if (custom) {
        if (!filteredSuggestions.some(([code]) => code === input)) {
          filteredSuggestions.unshift([input.toUpperCase(), custom]);
        }
      }
    }
  }

  const handleApplyCoupon = () => {
    setCouponError('');
    const code = couponCode.trim().toUpperCase();
    if (!code) {
      setCouponError('Enter a coupon code');
      return;
    }
    const coupon = couponDefinitions[code] || parseCustomCoupon(code);
    if (!coupon) {
      setCouponError('Invalid coupon');
      return;
    }
    setAppliedCoupon(coupon);
    setShowSuggestions(false);
  };

  const handleRemoveCoupon = () => {
    setAppliedCoupon(null);
    setCouponCode('');
    setCouponError('');
  };

  const loadRazorpayScript = () => {
    return new Promise((resolve) => {
      const script = document.createElement('script');
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });
  };

  // On payment success, send all data to Formspree, same as Pay Later flow
  const submitToFormspree = async (paymentData) => {
    setIsSubmittingPayLater(true);
    setFormspreeStatus(null);
    const payload = {
      name,
      mobile,
      ...state,
      academicLevel: selectedLevel,
      subtotal: discountedSubtotal,
      processingFee,
      total,
      coupon: appliedCoupon ? { code: couponCode.toUpperCase(), ...appliedCoupon } : null,
      specialistMultiplier: levelMultiplier,
      paymentMethod: 'Paid via Razorpay',
      paymentId: paymentData.razorpay_payment_id,
      submittedAt: new Date().toISOString(),
    };
    try {
      const response = await fetch('https://formspree.io/f/xeoldevz', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(payload),
      });
      if (response.ok) {
        setFormspreeStatus('success');
        alert('Payment succeeded and order saved!');
        navigate('/payment-success', { state: { ...state, paymentId: paymentData.razorpay_payment_id } });
      } else {
        setFormspreeStatus('error');
        alert('Payment succeeded but failed to save order. Please contact support.');
      }
    } catch (error) {
      setFormspreeStatus('error');
      alert('Payment succeeded but an error occurred saving your order.');
    }
    setIsSubmittingPayLater(false);
  };

  const handleNext = async () => {
    if (!name.trim()) {
      alert('Please enter your name.');
      return;
    }
    if (!mobile.trim()) {
      alert('Please enter your mobile number.');
      return;
    }

    const res = await loadRazorpayScript();
    if (!res) {
      alert('Failed to load Razorpay SDK. Check your internet connection.');
      return;
    }

    const options = {
      key: 'rzp_live_2bhBKQxbHgyb7a', // Replace with your Razorpay key ID
      amount: Math.round(total * 100),
      currency: 'INR',
      name: 'Infinte Soloution ',
      description: 'Assignment Payment',
      prefill: { name, contact: mobile },
      theme: { color: '#373057' },
      handler: function (response) {
        submitToFormspree(response);
      },
      modal: {
        ondismiss: function () {
          alert('Payment popup closed.');
        },
      },
    };

    const paymentObject = new window.Razorpay(options);
    paymentObject.open();
  };

  const onCouponKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleApplyCoupon();
    }
  };

  const handlePayLater = async () => {
    if (!name.trim()) {
      alert('Please enter your name.');
      return;
    }
    if (!mobile.trim()) {
      alert('Please enter your mobile number.');
      return;
    }
    setIsSubmittingPayLater(true);
    setFormspreeStatus(null);

    const payload = {
      name,
      mobile,
      ...state,
      academicLevel: selectedLevel,
      subtotal: discountedSubtotal,
      processingFee,
      total,
      coupon: appliedCoupon ? { code: couponCode.toUpperCase(), ...appliedCoupon } : null,
      specialistMultiplier: levelMultiplier,
      paymentMethod: 'Pay Later',
      submittedAt: new Date().toISOString(),
    };

    try {
      const response = await fetch('https://formspree.io/f/xeoldevz', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        setFormspreeStatus('success');
        alert('Your order is saved! You can pay later.');
      } else {
        setFormspreeStatus('error');
        alert('Failed to save your order. Please try again.');
      }
    } catch (error) {
      setFormspreeStatus('error');
      alert('An error occurred while saving your order.');
    }
    setIsSubmittingPayLater(false);
  };

  return (
    <div className="min-h-screen bg-[#02042b] py-16 mt-16">
      <div className="max-w-6xl mx-auto px-4">
        <h1 className="text-4xl font-bold text-center text-white mb-4">
          Pick Your Academic Specialist
        </h1>
        <p className="text-center text-lg text-gray-300 mb-8">
          (Specify your needs to ensure our writers deliver flawless work)
        </p>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Left side: inputs and selections */}
          <div className="space-y-4">
            {/* Name input */}
            <div>
              <label htmlFor="name" className="block text-gray-100 font-semibold mb-2">
                Full Name *
              </label>
              <input
                type="text"
                id="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Enter your full name"
                className="w-full rounded-lg p-3 text-black"
              />
            </div>
            {/* Mobile input */}
            <div>
              <label htmlFor="mobile" className="block text-gray-100 font-semibold mb-2">
                Mobile Number *
              </label>
              <input
                type="tel"
                id="mobile"
                value={mobile}
                onChange={(e) => setMobile(e.target.value)}
                placeholder="Enter your mobile number"
                className="w-full rounded-lg p-3 text-black"
              />
            </div>
            {/* Specialist selection */}
            {[
              {
                level: 'Top Scholar',
                desc: 'Choose from our top 10 PhD experts for advanced projects.',
                icon: '💎',
              },
              {
                level: 'Proficient Researcher',
                desc: 'Choose from our top 20 skilled experts for structured essays, papers, and reports.',
                icon: '👑',
              },
              {
                level: 'Skilled Academic Writer',
                desc: 'Get precise, well-referenced academic writing for coursework and analysis.',
                icon: '📘',
              },
            ].map(({ level, desc, icon }) => (
              <div
                key={level}
                className={`border rounded-xl p-6 flex justify-between items-center shadow-lg ${
                  selectedLevel === level ? 'border-[#373057] bg-gray-100' : 'bg-white'
                }`}
              >
                <div>
                  <h3 className="text-xl font-semibold text-gray-800 flex items-center gap-2">
                    {icon} {level}
                  </h3>
                  <p className="text-gray-600 text-sm mt-1">{desc}</p>
                </div>
                <button
                  onClick={() => setSelectedLevel(level)}
                  className={`px-5 py-2 rounded-full text-sm ${
                    selectedLevel === level
                      ? 'bg-[#373057] text-white'
                      : 'border border-[#373057] text-[#373057]'
                  }`}
                >
                  {selectedLevel === level ? 'Selected' : 'Select'}
                </button>
              </div>
            ))}
            {/* Navigation buttons */}
            <div className="flex justify-between mt-6 gap-4">
              <button
                onClick={() => navigate(-1)}
                className="px-6 py-2 rounded-lg border border-gray-300 text-[#f4f3f7]"
              >
                ← Back
              </button>
              <button
                onClick={handleNext}
                className="px-6 py-2 bg-[#373057] text-white rounded-lg flex items-center gap-2"
              >
                Proceed Payment
              </button>
              <button
                onClick={handlePayLater}
                disabled={isSubmittingPayLater}
                className="px-6 py-2 bg-yellow-500 text-black rounded-lg hover:bg-yellow-600 transition"
              >
                {isSubmittingPayLater ? 'Submitting...' : 'Pay Later'}
              </button>
            </div>
          </div>
          {/* Right side: summary and coupon */}
          <div>
            <div className="flex justify-end mb-4">
              <button className="px-6 py-2 rounded-full bg-[#1f2937] text-white font-medium">
                Order Information
              </button>
            </div>
            <div className="bg-white rounded-3xl p-6 shadow-xl relative">
              <div className="absolute top-4 right-4 flex flex-col items-center">
                <div className="h-10 w-10 bg-orange-500 rounded-full flex items-center justify-center text-white font-bold">
                  ✓
                </div>
                <div className="h-16 border-r-4 border-orange-500 my-1" />
                <div className="h-10 w-10 bg-orange-500 rounded-full flex items-center justify-center text-white font-bold">
                  ✓
                </div>
                <div className="h-16 border-r-4 border-orange-500 my-1" />
                <div className="h-10 w-10 bg-orange-500 rounded-full flex items-center justify-center text-white font-bold">
                  3
                </div>
              </div>
              <h2 className="text-2xl font-semibold text-gray-800 mb-2">
                Provide your order details
              </h2>
              <p className="text-gray-600 mb-1"><strong>Name:</strong> {name || '–'}</p>
              <p className="text-gray-600 mb-1"><strong>Mobile:</strong> {mobile || '–'}</p>
              <p className="text-gray-600 mb-1"><strong>Assignment Type:</strong> {state.assignmentType}</p>
              <p className="text-gray-600 mb-1"><strong>Subject:</strong> {state.subject}</p>
              <p className="text-gray-600 mb-1"><strong>Deadline:</strong> {state.deadline}</p>
              <p className="text-gray-600 mb-1"><strong>D:</strong> {state.filename}</p>
              <p className="text-gray-600 mb-1"><strong>Word/Page Count:</strong> {state.wordCount} / {state.pages}</p>
              <p className="text-gray-600 mb-1"><strong>Academic Level:</strong> {selectedLevel}</p>
              <p className="text-gray-600 mb-1"><strong>Specialist Multiplier:</strong> {levelMultiplier}x</p>
              <p className="text-gray-600 mb-1"><strong>Base Price from Task:</strong> INR {taskTotalPrice || 0}</p>
              <div className="mt-6 border-t pt-4 text-gray-800">
                <p className="mb-1"><strong>Subtotal (multiplied):</strong> INR {baseSubtotal.toLocaleString()}</p>
                {discountAmount > 0 && (
                  <p className="mb-1 text-green-700"><strong>Coupon discount:</strong> - INR {discountAmount.toLocaleString()}</p>
                )}
                <p className="mb-1"><strong>Discounted Subtotal:</strong> INR {discountedSubtotal.toLocaleString()}</p>
                <p className="mb-1"><strong>Processing Fee (18%):</strong> INR {processingFee.toLocaleString()}</p>
                <p className="font-semibold text-lg"><strong>Total:</strong> INR {total.toLocaleString()}</p>
              </div>
              {formspreeStatus === 'success' && (
                <p className="mt-4 text-green-600 font-semibold">Order saved successfully. You can pay later.</p>
              )}
              {formspreeStatus === 'error' && (
                <p className="mt-4 text-red-600 font-semibold">Failed to save order. Please try again.</p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Specialist;
